'use client';

import Image from 'next/image';
import {useTranslations} from 'next-intl';
import {type FC, memo} from 'react';
import {StatIcon} from '@/components/atoms/StatIcon';
import {Modal} from '@/utils/antd-imports';

interface CelebrityShareSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  reward: {coins: number; poe: number};
}

/**
 * @description Success modal after sharing celebrity (platform_share_celebrity). Figma 2825-263379.
 */
export const CelebrityShareSuccessModal: FC<CelebrityShareSuccessModalProps> = memo(({isOpen, onClose, reward}) => {
  const t = useTranslations('experiences.card');
  const hasCoins = reward.coins > 0;
  const hasPoe = reward.poe > 0;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      className="[&_.ant-modal-content]:!bg-transparent [&_.ant-modal-content]:!p-0 [&_.ant-modal-wrap]:!bg-black/60 [&_.ant-modal-content]:max-w-[288px] md:[&_.ant-modal-content]:max-w-[560px] [&_.ant-modal-content]:mx-auto"
      styles={{
        body: {
          padding: 0,
        },
      }}
      width={'100%'}
    >
      <div className="relative flex flex-col gap-4 rounded-[24px] bg-[#1E1E2A] px-6 py-7 md:gap-6 md:p-8 font-['Inter']">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 flex size-10 items-center justify-center rounded-full border border-[rgba(233,236,240,0.2)] p-2 transition-opacity hover:opacity-80 md:right-4 md:top-4"
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <title>Close</title>
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="#E9ECF0"
              strokeWidth="1.35"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="flex flex-col items-center gap-3 px-9 pt-1 md:gap-4 md:px-12 md:pt-0">
          <h2 className="text-center font-['inter'] text-[20px] font-bold leading-7 text-[#E9ECF0] md:text-[36px] md:leading-normal">
            {t('congratulations')}
          </h2>
        </div>

        <div className="flex flex-col gap-5 md:gap-8">
          <p className="text-center font-['inter'] text-[16px] font-medium leading-5 text-[rgba(233,236,240,0.8)] md:text-[24px] md:leading-normal">
            {t('you_earned')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            {hasCoins && (
              <div className="flex h-14 items-center gap-1.5 rounded-[60px] bg-[rgba(56,56,71,0.4)] pl-4 pr-6 py-3 md:h-[72px] md:gap-2 md:pl-5 md:pr-7 md:py-[15px] md:rounded-[80px]">
                <StatIcon type="coins" className="size-8 md:size-10" />
                <span className="font-['inter'] text-[28px] font-semibold leading-[1.16] text-[#E9ECF0] md:text-[36px] md:leading-[1.12]">
                  {reward.coins}
                </span>
              </div>
            )}
            {hasPoe && (
              <div className="flex h-14 items-center gap-1.5 rounded-[60px] bg-[rgba(56,56,71,0.4)] pl-4 pr-6 py-3 md:h-[72px] md:gap-2 md:pl-5 md:pr-7 md:py-[15px] md:rounded-[80px]">
                <StatIcon type="fanTokens" className="size-8 md:size-10" />
                <span className="font-['inter'] text-[28px] font-semibold leading-[1.16] text-[#E9ECF0] md:text-[36px] md:leading-[1.12]">
                  {reward.poe}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="pt-2 md:pt-5">
          <button
            type="button"
            onClick={onClose}
            className="flex h-12 w-full items-center justify-center rounded-[14px] bg-[#E9ECF0] font-montserrat text-base font-bold uppercase tracking-[0.32px] text-[#1E1E2A] transition-opacity hover:opacity-90 md:h-[60px] md:text-[19px] md:leading-[1.2]"
          >
            {t('ok')}
          </button>
        </div>
      </div>
    </Modal>
  );
});

CelebrityShareSuccessModal.displayName = 'CelebrityShareSuccessModal';
